/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

// Celtra rich-media banner support (e.g. Future's "Horizon" anchored format,
// GAM creative template "CLS Compliant - Celtra - Horizon 428x150"). One file
// per vendor format lives under formats/vendor/<vendor>/.

// All selectors below are owned by the Celtra runtime (web.js) or the GAM
// creative template that bootstraps it, not by publishers, so they hold
// across properties.

export const CELTRA_VENDOR = 'celtra';

// `div.celtra-ad-v3` is part of the GAM creative markup itself, so it is
// present synchronously at render; the runtime then adds the inline-host
// class, the banner div and the per-render `celtra-script-<n>` script.
export const CELTRA_MARKER_SELECTOR =
  '.celtra-ad-v3, .celtra-ad-inline-host, .celtra-banner, ' +
  'script[id^="celtra-script-"], script[src*="ads.celtra.com"]';

// Opportunistic signature for Prebid bid `ad` markup.
export const BID_MARKUP_SIGNATURE = /celtra-ad-v3|ads\.celtra\.com/i;

// The Celtra creative id is the path segment of the runtime script url:
// https://ads.celtra.com/<id>/web.js?...
export const SCRIPT_SRC_ID_PATTERN = /ads\.celtra\.com\/([0-9a-z]+)\/web\.js/i;

// Bounded re-check window: the runtime builds its srcdoc iframes after
// web.js loads, typically well inside 2s of the render.
export const DEFAULT_DETECT_MS = 2000;

// Re-check points, as fractions of the detect window.
export const RECHECK_STEPS = [0.125, 0.25, 0.5, 1];

// The creative UI lives in srcdoc iframes nested inside the rendered ad
// iframe; one extra level is tolerated for expanded/reveal layers.
export const MAX_FRAME_DEPTH = 3;

// Escaped creative markup that renders directly in the host document when the
// runtime co-opts the slot container (no iframe to listen on).
export const ESCAPED_HOST_SELECTOR = '.celtra-ad-inline-host, .celtra-banner';

export const INTERACTION_TRIGGER = 'CeltraInteractionTriggerInnerFrameClick';

// Cross-origin surfaces: inferred from focus/hover rather than a real click.
export const OPAQUE_INTERACTION_TRIGGER = 'CeltraInteractionTriggerBlurOpaqueSurface';

type Surfaces = {
  docs: Document[];
  elements: Element[];
  opaque: HTMLIFrameElement[];
};

import { Logger } from '../../../../logger';
import type { FormatDetection } from '../../../../types/formats';
import type { FormatInteractionCallback, VendorFormatAdapter } from '../../index';

const logger = Logger.getInstance();

/**
 * Detects Celtra rich-media renders and captures clicks on the creative
 * surfaces the standard placement listeners cannot see.
 *
 * Celtra builds the ad UI inside `srcdoc` iframes. Pointer events land in the
 * innermost document and never propagate across the iframe boundary, and the
 * clickthrough is opened from that inner context — so the placement's
 * friendly-document click listener and `contentWindow.open` override both
 * stay silent (verified against Future's Horizon 428x150 template).
 *
 * Crucially, the runtime commonly ESCAPES the GAM friendly iframe and co-opts
 * the slot container, leaving `div.celtra-ad-inline-host > div.celtra-banner >
 * iframe[srcdoc]` directly in the host document (verified on live Future
 * pages, 2026-07-28). The click surface is then the FIRST-level frame under
 * the container, not something nested inside the ad frame — so this adapter
 * arms every same-origin document it can reach beneath the container, plus
 * escaped markup rendered directly in the host document. Where a surface is
 * cross-origin and cannot be listened to at all, it falls back to the
 * Adnami-style blur + activeElement/hover heuristic.
 *
 * Arming is cumulative across a bounded rescan window: the runtime adds frames
 * after the initial render, and a click inside a late frame would not bubble
 * to anything armed earlier. Interactions are routed through the placement
 * latch and the navigation-evidence gate like every other candidate.
 *
 * Unlike Adnami, Celtra exposes no top-window global — its runtime lives
 * entirely inside the ad iframe — so the registry probe cannot rule the
 * vendor out per page and this adapter is created unconditionally; relevance
 * is decided per render by inspectRender's marker scan.
 *
 * One instance is owned by one Placement and is scoped to a single render:
 * the owning placement disposes it on re-render.
 */
class CeltraBannerAdapter implements VendorFormatAdapter {
  public readonly vendor = CELTRA_VENDOR;

  private container: HTMLElement | null = null;
  private detection: FormatDetection | null = null;

  private detectionTimers: number[] = [];
  private armingTimers: number[] = [];

  private armedDocs: Document[] = [];
  private armedElements: Element[] = [];
  private opaqueFrames: HTMLIFrameElement[] = [];
  private innerClickListener?: () => void;
  private surfaceMouseEnterListener?: () => void;
  private surfaceMouseLeaveListener?: () => void;
  private winBlurListener?: () => void;
  private mouseOverSurface = false;

  /**
   * Synchronous detection against the placement's slot container and its
   * rendered friendly iframe document, plus the optional Prebid bid markup.
   * Returns the current best detection, or null when nothing Celtra is found.
   */
  public inspectRender(
    container: HTMLElement | null,
    bidAdMarkup?: string,
  ): FormatDetection | null {
    this.container = container;

    let markerRoot: ParentNode | null = null;
    if (container) {
      if (container.querySelector(CELTRA_MARKER_SELECTOR) !== null) {
        markerRoot = container;
      } else {
        // The GAM template renders into a friendly iframe, so its document is
        // inspectable. Cross-origin access throws; treat as not-detected.
        const adFrame = container.querySelector('iframe');
        try {
          const frameDoc = adFrame?.contentWindow?.document;
          if (frameDoc && frameDoc.querySelector(CELTRA_MARKER_SELECTOR) !== null) {
            markerRoot = frameDoc;
          }
        } catch (e) {}
      }
    }

    if (!markerRoot && !(bidAdMarkup && BID_MARKUP_SIGNATURE.test(bidAdMarkup))) {
      return null;
    }

    this.detection = {
      vendor: this.vendor,
      formatId: markerRoot ? this.findFormatId(markerRoot) : null,
      groupId: markerRoot ? this.findGroupId(markerRoot) : null,
    };
    return this.detection;
  }

  /**
   * Bounded re-checks for markers that appear after the render (web.js loads
   * and builds the banner asynchronously). Invokes the callback at most once,
   * with the first complete (or final best) detection.
   */
  public watchForLateDetection(
    container: HTMLElement | null,
    callback: (detection: FormatDetection) => void,
  ): void {
    this.clearTimers(this.detectionTimers);
    let notified = false;

    const notify = (detection: FormatDetection) => {
      if (!notified) {
        notified = true;
        this.clearTimers(this.detectionTimers);
        callback(detection);
      }
    };

    RECHECK_STEPS.forEach((step, index) => {
      const isLastStep = index === RECHECK_STEPS.length - 1;
      const timer = window.setTimeout(() => {
        const detection = this.inspectRender(container);
        if (
          detection &&
          ((detection.groupId !== null && detection.formatId !== null) || isLastStep)
        ) {
          notify(detection);
        }
      }, DEFAULT_DETECT_MS * step);
      this.detectionTimers.push(timer);
    });
  }

  /**
   * Arm click capture inside the creative's nested same-origin iframes. The
   * frames are injected asynchronously after web.js loads, so scanning
   * retries on the same bounded schedule as detection.
   */
  public armInteractionCapture(onInteraction: FormatInteractionCallback): void {
    this.clearTimers(this.armingTimers);

    // Rescan for the whole window rather than stopping at the first surface:
    // the runtime keeps building frames after the initial render, and a click
    // in a frame added later would not bubble to anything armed earlier.
    const scan = () => this.armNewSurfaces(this.findSurfaces(), onInteraction);

    scan();
    RECHECK_STEPS.forEach((step) => {
      this.armingTimers.push(window.setTimeout(scan, DEFAULT_DETECT_MS * step));
    });
  }

  public dispose(): void {
    this.clearTimers(this.detectionTimers);
    this.clearTimers(this.armingTimers);
    this.disarmSurfaces();
    this.container = null;
    this.detection = null;
  }

  /**
   * Arms any surface not already armed. Idempotent, so the bounded rescan can
   * pick up frames the runtime adds after the initial render.
   */
  private armNewSurfaces(surfaces: Surfaces, onInteraction: FormatInteractionCallback): void {
    if (!this.innerClickListener) {
      this.innerClickListener = () => {
        this.fireInteraction(onInteraction, INTERACTION_TRIGGER);
      };
    }

    let added = 0;
    for (const doc of surfaces.docs) {
      if (!this.armedDocs.includes(doc)) {
        // Capture phase: Celtra stops propagation on some of its own handlers.
        doc.addEventListener('click', this.innerClickListener, true);
        this.armedDocs.push(doc);
        added++;
      }
    }
    for (const el of surfaces.elements) {
      if (!this.armedElements.includes(el)) {
        el.addEventListener('click', this.innerClickListener, true);
        this.armedElements.push(el);
        added++;
      }
    }

    if (added > 0) {
      // A real click listener always beats the focus heuristic.
      this.disarmOpaqueFallback();
      logger.debug(
        `Celtra interaction capture armed on ${this.armedDocs.length} document(s) and ` +
          `${this.armedElements.length} escaped element(s)`,
      );
      return;
    }

    if (this.armedDocs.length === 0 && this.armedElements.length === 0) {
      this.armOpaqueFallback(surfaces.opaque, onInteraction);
    }
  }

  /**
   * Cross-origin fallback: the creative's surfaces cannot be listened to
   * directly, so infer the click from focus leaving the page while it sits in
   * (or the pointer is over) one of the opaque frames — the same heuristic the
   * Adnami adapter uses for its sandboxed rails.
   */
  private armOpaqueFallback(
    frames: HTMLIFrameElement[],
    onInteraction: FormatInteractionCallback,
  ): void {
    if (frames.length === 0 || this.opaqueFrames.length > 0) {
      return;
    }
    this.opaqueFrames = frames;
    this.surfaceMouseEnterListener = () => {
      this.mouseOverSurface = true;
    };
    this.surfaceMouseLeaveListener = () => {
      this.mouseOverSurface = false;
    };
    frames.forEach((frame) => {
      frame.addEventListener('mouseenter', this.surfaceMouseEnterListener!);
      frame.addEventListener('mouseleave', this.surfaceMouseLeaveListener!);
    });
    this.winBlurListener = () => {
      const activeElement = document.activeElement;
      if (
        (activeElement && this.opaqueFrames.some((f) => f === activeElement)) ||
        this.mouseOverSurface
      ) {
        this.fireInteraction(onInteraction, OPAQUE_INTERACTION_TRIGGER);
      }
    };
    window.addEventListener('blur', this.winBlurListener);
    logger.debug(`Celtra opaque-surface fallback armed on ${frames.length} frame(s)`);
  }

  private fireInteraction(onInteraction: FormatInteractionCallback, trigger: string): void {
    // One-shot per render: the owning placement's per-render latch also
    // dedupes, but removing the listeners avoids repeat work on later clicks.
    this.disarmSurfaces();
    onInteraction(trigger);
  }

  private disarmSurfaces(): void {
    if (this.innerClickListener) {
      this.armedDocs.forEach((doc) =>
        doc.removeEventListener('click', this.innerClickListener!, true),
      );
      this.armedElements.forEach((el) =>
        el.removeEventListener('click', this.innerClickListener!, true),
      );
    }
    this.disarmOpaqueFallback();
    this.armedDocs = [];
    this.armedElements = [];
    this.innerClickListener = undefined;
  }

  private disarmOpaqueFallback(): void {
    this.opaqueFrames.forEach((frame) => {
      if (this.surfaceMouseEnterListener) {
        frame.removeEventListener('mouseenter', this.surfaceMouseEnterListener);
      }
      if (this.surfaceMouseLeaveListener) {
        frame.removeEventListener('mouseleave', this.surfaceMouseLeaveListener);
      }
    });
    if (this.winBlurListener) {
      window.removeEventListener('blur', this.winBlurListener);
    }
    this.opaqueFrames = [];
    this.surfaceMouseEnterListener = undefined;
    this.surfaceMouseLeaveListener = undefined;
    this.winBlurListener = undefined;
    this.mouseOverSurface = false;
  }

  /**
   * Every clickable surface belonging to this render, found by a depth-capped
   * walk of the slot container.
   *
   * Production shape (verified on Future, 2026-07-28): the creative commonly
   * ESCAPES the GAM friendly iframe and co-opts the slot container, leaving
   * `div.celtra-ad-inline-host > div.celtra-banner > iframe[srcdoc]` directly
   * in the host document. Clicks then land in that first-level frame — so,
   * unlike the original implementation, first-level frame documents are
   * included rather than skipped. Double-arming the GPT frame's own document
   * (the non-escaped shape) is harmless: the placement's per-render latch
   * dedupes, and this adapter only arms once Celtra has been detected.
   *
   * Escaped markup that renders directly in the host document (no iframe) is
   * covered by element-level listeners, and frames we cannot enter are
   * returned separately for the blur/hover fallback.
   */
  private findSurfaces(): Surfaces {
    const docs: Document[] = [];
    const opaque: HTMLIFrameElement[] = [];
    const container = this.container;
    if (!container) {
      return { docs, elements: [], opaque };
    }

    let frontier: ParentNode[] = [container];
    for (let depth = 0; depth < MAX_FRAME_DEPTH && frontier.length > 0; depth++) {
      const next: ParentNode[] = [];
      for (const root of frontier) {
        for (const frame of root.querySelectorAll('iframe')) {
          let frameDoc: Document | null = null;
          try {
            frameDoc = frame.contentWindow?.document ?? null;
          } catch (e) {}
          if (frameDoc) {
            docs.push(frameDoc);
            next.push(frameDoc);
          } else {
            opaque.push(frame);
          }
        }
      }
      frontier = next;
    }

    // Escaped Celtra markup sitting directly in the host document.
    const elements = [...container.querySelectorAll(ESCAPED_HOST_SELECTOR)];
    return { docs, elements, opaque };
  }

  private findFormatId(root: ParentNode): string | null {
    const script = root.querySelector<HTMLScriptElement>('script[src*="ads.celtra.com"]');
    const match = script?.src.match(SCRIPT_SRC_ID_PATTERN);
    return match ? match[1] : null;
  }

  private findGroupId(root: ParentNode): string | null {
    // The bootstrap assigns each injected runtime script a per-render id,
    // `celtra-script-<n>` (n = window.celtraScriptIndex in the ad iframe).
    return root.querySelector('script[id^="celtra-script-"]')?.id ?? null;
  }

  private clearTimers(timers: number[]): void {
    timers.forEach((timer) => window.clearTimeout(timer));
    timers.length = 0;
  }
}

export { CeltraBannerAdapter };
